import { useEffect, useRef, useState } from 'react';

/**
 * Custom hook for managing sessionStorage.
 *
 * @param {string} key - The key under which the value is stored in sessionStorage.
 * @param {*} defaultValue - The default value to use if none is found or the item has expired.
 * @param {number} expiryTime - Optional expiration time in milliseconds..
 * @returns {[any, Function]} - Returns the stored value and a setter function.
 */
const useSessionStorage = (key, defaultValue, expiryTime = null) => {
  const [storedValue, setStoredValue] = useState(defaultValue);
  const timeoutRef = useRef(null);
  const isMountedRef = useRef(true);

  const clearExistingTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      clearExistingTimeout();
    };
  }, []);

  useEffect(() => {
    const { value: existingValue, expiry } = getStoredValue(key, defaultValue);
    setStoredValue(existingValue);
    clearExistingTimeout();

    if (expiryTime === null || !expiry) {
      return;
    }

    const timeLeft = expiry - Date.now();

    if (timeLeft <= 0) {
      sessionStorage.removeItem(key);
      if (isMountedRef.current) {
        setStoredValue(defaultValue);
      }
    } else {
      timeoutRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;
        sessionStorage.removeItem(key);
        setStoredValue(defaultValue);
      }, timeLeft);
    }

    return () => {
      clearExistingTimeout();
    };
  }, [key]);

  const setValue = (updateFnOrValue) => {
    const isClient = () => typeof window !== 'undefined';
    if (!isClient()) return;

    try {
      clearExistingTimeout();

      const newValue =
        typeof updateFnOrValue === 'function'
          ? updateFnOrValue(storedValue)
          : updateFnOrValue;

      const expiry = expiryTime !== null ? Date.now() + expiryTime : null;

      const sessionStorageItem = {
        value: newValue,
        expiry,
      };

      sessionStorage.setItem(key, JSON.stringify(sessionStorageItem));
      setStoredValue(newValue);

      if (expiryTime !== null) {
        timeoutRef.current = setTimeout(() => {
          if (!isMountedRef.current) return;
          sessionStorage.removeItem(key);
          setStoredValue(defaultValue);
        }, expiryTime);
      }
    } catch (error) {
      console.error(`Error setting sessionStorage key "${key}":`, error);
    }
  };

  return [storedValue, setValue];
};

const getStoredValue = (key, defaultValue) => {
  const defaultValues = { value: defaultValue, expiry: null };

  try {
    const sessionStorageItem = sessionStorage.getItem(key);
    if (!sessionStorageItem) return defaultValues;

    const foundItem = JSON.parse(sessionStorageItem);

    if (isItemExpired(foundItem)) {
      sessionStorage.removeItem(key);
      return defaultValues;
    }

    return { value: foundItem.value, expiry: foundItem.expiry };
  } catch (error) {
    console.error(`Error reading sessionStorage key "${key}":`, error);
    return defaultValues;
  }
};

const isItemExpired = (item) => {
  if (!item?.expiry) return false;
  const now = Date.now();
  return now > item.expiry;
};

export default useSessionStorage;
